import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { Dashboard } from "../components/Dashboard/Dashboard";
import { GraphicsTemplates } from "../components/Dashboard/GraphicsTemplates/GraphicsTemplates";
import { ReportsList } from "../components/Report/Lists/ReportsList";
import { ReportViewer } from "../components/Report/All/ReportViewer";
import { ProfileScreen } from "../components/Profile/ProfileScreen";

import { Users } from "../components/Users/Users";
import { User } from "../components/Users/User";
import { RolesUser } from "../components/Users/Roles/RolesUser";
import { NewRoleUser } from "../components/Users/Roles/NewRoleUser";
import { Segmentation } from "../components/Users/Segmentation/Segmentation";
import { SegmentationList } from "../components/Users/Segmentation/SegmentationList";
import { MenusUser } from "../components/Users/Menus/MenusUser";
import { NewMenuUser } from "../components/Users/Menus/NewMenuUser";
import { PermitsUser } from "../components/Users/Permits/PermitsUser";

import { Catalogues } from "../components/Catalogues/Catalogues";
import { FormsList } from "../components/JSONForms/FormsList/FormsList";
import { CreateForm } from "../components/JSONForms/CreateForm/CreateForm";
import { NewReport } from "../components/JSONForms/Report/NewReport";

import { Settings } from "../screens/Settings/Main/Settings";
import { BusinessUnit } from "../screens/Settings/BusinessUnit/BusinessUnit";
import { LandingPageSettings } from "../screens/Settings/LandingPageSettings/LandingPageSettings";
import { PeopleSettings } from "../screens/Settings/People/PeopleSettings";
import { WorkplaceSettings } from "../screens/Settings/Workplace/WorkplaceSettings";
import { DepartmentSettings } from "../screens/Settings/Departaments/DepartmentSettings";
import { ReportSettings } from "../screens/Settings/Reports/ReportSettings";
import { ClientSignup } from "../screens/Auth/ClientSignup";
import { NotFound } from "../screens/NotFound/NotFound";
import { BusinessUnitList } from "../screens/Settings/BusinessUnit/BusinessUnitList";
import { CreateCatalogue } from "../components/Catalogues/CreateCatalogue";

import { Bitacora } from "../components/MainComponents/Bitacora/Bitacora";
import { startGettingUserInfo } from "../store/auth/authThunks";
import { Exportables } from "../components/Exports/Main/Exports";
import { startSetContentRect } from "../store/theme/themeThunks";
import { EGLayout } from "../config/layout/EGLayout";
import { Box } from "@mui/material";
import ExportablesRefactor from "../components/Exports/Main/Exports";
import Builder from "../components/Exports/Builder/Builder";
import { Dashboardv2 } from "../components/Dashboard/Dashboardv2";
import { DashboardStatic } from "../components/Dashboard/DashboardStatic";
import { ReportProvider } from "../context/report/ReportContext";
import CustomDashboardList from "../screens/Analytics/CustomDashboards/CustomDashboardList";
import CustomDashboardViewer from "../screens/Analytics/CustomDashboards/CustomDashboardViewer";
import CustomDashboardBuilder from "../screens/Analytics/CustomDashboards/CustomDashboardBuilder";

export const UserRoutes = () => {
  const { status } = useSelector((state) => state.auth);
  const contentRef = useRef(null);
  const { fromUrl } = useSelector((state) => state.jaguar);
  const { gTheme } = useSelector((state) => state.theme);
  
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(startGettingUserInfo());
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;
    const updateContentRect = () => {
      dispatch(startSetContentRect(contentRef.current.getBoundingClientRect()));
    };

    updateContentRect();

    window.addEventListener("resize", updateContentRect);
    return () => {
      window.removeEventListener("resize", updateContentRect);
    };
  }, [status]);

  useEffect(() => {
    if (!contentRef) return;
    dispatch(startSetContentRect(contentRef.current.getBoundingClientRect()));
  }, [status]);

  useEffect(() => {
    if (fromUrl) {
      navigate(fromUrl);
    }
  }, [fromUrl]);

  return (
    <Box
      ref={contentRef}
      sx={{
        flex: "1 1 auto",
      }}
    >
      <Routes>
        <Route element={<EGLayout />}>
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/login" element={<Navigate to="/dashboard" />} />
          <Route path="/dashboard" element={<DashboardStatic />} />
          {/* Dynamic/customizable dashboards moved under Analytics */}
          <Route path="/analytics/dashboards" element={<Dashboardv2 />} />
          {/* BI Panel — dashboards with frozen results (no re-query at view time) */}
          <Route path="/analytics/custom-dashboards" element={<CustomDashboardList />} />
          <Route
            path="/analytics/custom-dashboards/:id"
            element={<CustomDashboardViewer />}
          />
          <Route
            path="/analytics/custom-dashboards/:id/edit"
            element={<CustomDashboardBuilder />}
          />
          <Route path="/profile" element={<ProfileScreen vTheme={gTheme} />} />
          <Route path="/reports/new" element={<NewReport />} />
          <Route path="/reports/all" element={<ReportsList />} />
          <Route path="/reports/all/:status" element={<ReportsList />} />
          <Route element={<ReportProvider />}>
            <Route
              path="/reports/tc/:id"
              element={<ReportViewer tc="true" />}
            />
            <Route path="/reports/:id" element={<ReportViewer />} />
          </Route>
          <Route path="/users" element={<Users />} />
          <Route path="/users/new" element={<User />} />
          <Route path="/users/edit/:id" element={<User />} />
          <Route
            path="/users/permits"
            element={<PermitsUser vTheme={gTheme} />}
          />
          <Route
            path="/users/roles/new"
            element={<NewRoleUser vTheme={gTheme} />}
          />
          <Route path="/users/menus" element={<MenusUser vTheme={gTheme} />} />
          <Route
            path="/users/menus/new"
            element={<NewMenuUser vTheme={gTheme} />}
          />
          <Route path="/users/segments" element={<SegmentationList />} />
          <Route path="/users/segments/new" element={<Segmentation />} />
          <Route path="/users/segments/:id" element={<Segmentation />} />
          <Route path="/users/roles" element={<RolesUser />} />
          <Route path="/formslist" element={<FormsList />} />
          <Route path="/createform" element={<CreateForm />} />
          <Route path="/editform/:id" element={<CreateForm />} />
          <Route path="/catalogues" element={<Catalogues />} />
          <Route path="/catalogues/new" element={<CreateCatalogue />} />
          <Route path="/clientsCr" element={<ClientSignup />} />
          <Route path="/settings" element={<Settings />} />
          <Route
            path="/settings/business/list"
            element={<BusinessUnitList />}
          />
          <Route path="/settings/business/create" element={<BusinessUnit />} />
          <Route path="/settings/business/:alias" element={<BusinessUnit />} />
          <Route
            path="/settings/department"
            element={<DepartmentSettings vTheme={gTheme} />}
          />
          <Route
            path="/settings/landing/:businessUnit"
            element={<LandingPageSettings vTheme={gTheme} />}
          />
          <Route
            path="/settings/people"
            element={<PeopleSettings vTheme={gTheme} />}
          />
          <Route
            path="/settings/reports"
            element={<ReportSettings vTheme={gTheme} />}
          />
          <Route
            path="/settings/workplace"
            element={<WorkplaceSettings vTheme={gTheme} />}
          />
          <Route path="/exports/" element={<ExportablesRefactor />} />
          <Route path="/exports/builder" element={<Builder />} />
          <Route path="/logs/" element={<Bitacora />} />
          <Route path="/auth-callback" element={<Navigate to="/" />} />
          <Route path="/*" element={<NotFound to="/notfound" />} />

          <Route
            path="/graphics/templates"
            element={<GraphicsTemplates vTheme={gTheme} />}
          />
        </Route>
      </Routes>
    </Box>
  );
};
